/**
 * Smart app banner — Android & iOS mobile PDP.
 */
(function () {
  const MOBILE_MQ = window.matchMedia('(max-width: 1023px)');

  function isAndroid() {
    return /Android/i.test(navigator.userAgent);
  }

  function isIOS() {
    return /(iPhone|iPad|iPod)/i.test(navigator.userAgent);
  }

  function getPlatform() {
    if (isAndroid()) return 'android';
    if (isIOS()) return 'ios';
    return null;
  }

  function isDesignMode(banner) {
    return banner.dataset.designMode === 'true' || (window.Shopify && Shopify.designMode);
  }

  /**
   * @param {string} storageKey
   */
  function isDismissed(storageKey) {
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return false;

      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed.expiresAt === 'number') {
        if (Date.now() < parsed.expiresAt) return true;
        localStorage.removeItem(storageKey);
        return false;
      }

      return raw === '1';
    } catch {
      return localStorage.getItem(storageKey) === '1';
    }
  }

  /**
   * @param {string} storageKey
   * @param {number} dismissDays
   */
  function setDismissed(storageKey, dismissDays) {
    const days = Number.isFinite(dismissDays) && dismissDays > 0 ? dismissDays : 30;
    const expiresAt = Date.now() + days * 24 * 60 * 60 * 1000;
    localStorage.setItem(storageKey, JSON.stringify({ expiresAt }));
  }

  /**
   * @param {HTMLElement} banner
   */
  function buildAndroidIntentUrl(banner) {
    const variantId = banner.dataset.variantId || '';
    const playStoreUrl =
      banner.dataset.playStoreUrl ||
      'https://play.google.com/store/apps/details?id=com.vgl.mobile.thejewelrychannel';
    const fallback = encodeURIComponent(playStoreUrl);

    return (
      'intent://m.app?screen=product&pid=' +
      encodeURIComponent(variantId) +
      '#Intent;scheme=tjcapp;package=com.vgl.mobile.thejewelrychannel;S.browser_fallback_url=' +
      fallback +
      ';end;'
    );
  }

  /**
   * @param {HTMLElement} banner
   */
  function buildIosAppUrl(banner) {
    const variantId = banner.dataset.variantId || '';
    return 'tjcapp://m.app?pid=' + encodeURIComponent(variantId);
  }

  /**
   * @param {HTMLElement} banner
   */
  function syncBannerHeight(banner) {
    if (!banner || banner.hidden) {
      document.body.style.setProperty('--smart-app-banner-height', '0px');
      return;
    }
    document.body.style.setProperty('--smart-app-banner-height', banner.offsetHeight + 'px');
  }

  /**
   * @param {HTMLElement} banner
   */
  function updateBannerLinks(banner) {
    const androidCta = banner.querySelector('.js-smart-app-banner-cta-android');
    if (androidCta instanceof HTMLAnchorElement) {
      androidCta.href = buildAndroidIntentUrl(banner);
    }

    const iosCta = banner.querySelector('.js-smart-app-banner-cta-ios');
    if (iosCta instanceof HTMLAnchorElement) {
      iosCta.href = buildIosAppUrl(banner);
    }
  }

  /**
   * @param {HTMLElement} banner
   * @param {string|number} variantId
   */
  function updateBannerVariant(banner, variantId) {
    if (!variantId) return;
    banner.dataset.variantId = String(variantId);
    updateBannerLinks(banner);
  }

  /**
   * @param {HTMLElement} banner
   */
  function initBanner(banner) {
    const storageKey = banner.dataset.storageKey || 'tjc-smart-app-banner-dismissed';
    const dismissDays = Number.parseInt(banner.dataset.dismissDays || '30', 10);
    const preview = isDesignMode(banner);
    const platform = getPlatform();

    if (!preview && (!platform || !MOBILE_MQ.matches || isDismissed(storageKey))) {
      banner.remove();
      return;
    }

    banner.dataset.platform = preview ? platform || 'ios' : platform;
    updateBannerLinks(banner);

    banner.querySelectorAll('.js-smart-app-banner-dismiss').forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (!preview) {
          setDismissed(storageKey, dismissDays);
        }
        banner.remove();
        syncBannerHeight(banner);
      });
    });

    whenBannerStylesReady(function () {
      if (!banner.isConnected) return;
      banner.hidden = false;
      syncBannerHeight(banner);

      if (typeof ResizeObserver !== 'undefined') {
        new ResizeObserver(function () {
          syncBannerHeight(banner);
        }).observe(banner);
      }
    });
  }

  function bannerStylesReady() {
    const link = document.querySelector('link[data-smart-app-banner-css]');
    if (!link) return true;
    return link.dataset.loaded === 'true' || link.media === 'all';
  }

  /**
   * @param {function(): void} callback
   */
  function whenBannerStylesReady(callback) {
    if (bannerStylesReady()) {
      callback();
      return;
    }

    const link = document.querySelector('link[data-smart-app-banner-css]');
    if (!link) {
      callback();
      return;
    }

    link.addEventListener('load', function onReady() {
      if (!bannerStylesReady()) return;
      link.removeEventListener('load', onReady);
      callback();
    });
  }

  function onVariantChange(event) {
    const variantId = event.detail?.resource?.id;
    if (!variantId) return;

    document.querySelectorAll('[data-smart-app-banner]').forEach(function (banner) {
      updateBannerVariant(banner, variantId);
    });
  }

  document.addEventListener('variant:update', onVariantChange);

  function boot() {
    document.querySelectorAll('[data-smart-app-banner]').forEach(initBanner);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
